import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import type {
  HrmsAttendanceSettingsDto,
  HrmsAttendanceSettingsUpdatePayload,
  HrmsBiometricStatusResponse,
} from '../types/hrmsAttendanceSettings.types'

const formatMetaDate = (dateStr?: string): string => {
  if (!dateStr) return 'Recently'
  try {
    const d = new Date(dateStr)
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
  } catch {
    // Fallback
  }
  return String(dateStr)
}

const enrichDto = (dto: any): HrmsAttendanceSettingsDto => {
  const halfDayMinutes = dto?.halfDayThresholdMins ?? 240
  const halfDayHours = Number((halfDayMinutes / 60).toFixed(2))

  return {
    ...dto,
    meta: {
      lastUpdatedBy: 'Admin',
      lastUpdatedAt: formatMetaDate(dto?.updatedAt),
      version: '2.4',
    },
    source: {
      primarySource: dto?.biometricEnabled ? 'Biometric Gateway' : 'Manual Entry',
      syncFrequency: 'Real-time',
      lastSyncedAt: formatMetaDate(dto?.updatedAt),
    },
    settings: {
      gracePeriodMinutes: dto?.arrivalGracePeriodMins ?? 15,
      lateThresholdMinutes: dto?.lateArrivalThresholdMins ?? 30,
      halfDayHours,
      dailyWorkingHours: dto?.dailyWorkingHours ?? 8,
      absentCutoff: (dto?.defaultAbsentTime || '19:00:00').slice(0, 5),
      shiftMode: dto?.shiftMode?.displayName ?? 'Fixed Academic Hours',
      shiftModeId: dto?.shiftModeId,
      workStart: (dto?.workStartTime || '09:00:00').slice(0, 5),
      workEnd: (dto?.workEndTime || '17:00:00').slice(0, 5),
      biometricEnabled: dto?.biometricEnabled ?? true,
      manualExcelEnabled: dto?.manualExcelEnabled ?? true,
      selfServiceEnabled: true,
      managerApprovalRequired: true,
      adminOverrideEnabled: false,
    },
  }
}

/**
 * Fetch HRMS Attendance Settings configuration.
 * Returns defaults if unconfigured.
 */
export const getAttendanceSettings = async (): Promise<HrmsAttendanceSettingsDto> => {
  const { data } = await apiClient.get<HrmsAttendanceSettingsDto | { data: HrmsAttendanceSettingsDto }>(
    ENDPOINTS.HRMS.ATTENDANCE_SETTINGS
  )
  const raw = (data as any)?.data ?? data
  return enrichDto(raw)
}

/**
 * Save/upsert HRMS Attendance Settings configuration.
 * Body contains only allowed configuration keys.
 */
export const saveAttendanceSettings = async (
  payload: HrmsAttendanceSettingsUpdatePayload
): Promise<HrmsAttendanceSettingsDto> => {
  const { data } = await apiClient.put<HrmsAttendanceSettingsDto | { data: HrmsAttendanceSettingsDto }>(
    ENDPOINTS.HRMS.ATTENDANCE_SETTINGS,
    payload
  )
  const raw = (data as any)?.data ?? data
  return enrichDto(raw)
}

/**
 * Fetch Biometric hardware gateway status.
 */
export const getBiometricStatus = async (): Promise<HrmsBiometricStatusResponse> => {
  const { data } = await apiClient.get<HrmsBiometricStatusResponse | { data: HrmsBiometricStatusResponse }>(
    ENDPOINTS.HRMS.ATTENDANCE_BIOMETRIC_STATUS
  )
  return (data as any)?.data ?? data
}

// Aliases for feature flexibility
export const updateAttendanceSettings = saveAttendanceSettings
export const resetAttendanceSettings = getAttendanceSettings
export const fetchAttendanceSettings = getAttendanceSettings
export const fetchBiometricStatus = getBiometricStatus
